import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ProductDocuments from '../components/ProductDocuments';
import ProjectVideo from '../components/home/ProjectVideo';
import { AW01_INSTALL_VIDEO } from '../data/productContent';
import { BRACKET_COUNT } from '../config/installation';
import useInvisibleRecaptcha from '../hooks/useInvisibleRecaptcha';
import { RECAPTCHA_SITE_KEY } from '../config/env';
import { apiFetch } from '../services/api';
import './ForInstallersPage.css';

// The manual and wind report are attached to the AW01 product, so they are read
// from there rather than hardcoded - if they are re-uploaded through the admin,
// this page follows automatically.
const DOCS_SOURCE_SLUG = 'aw01-full-box-awning';

export default function ForInstallersPage() {
  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formRef = useRef(null);

  useEffect(() => {
    apiFetch(`/api/products/${DOCS_SOURCE_SLUG}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => setDocuments((p && p.documents) || []))
      .catch(() => {});
  }, []);

  const submitForm = useCallback(async (token) => {
    const form = formRef.current;
    if (!form) return;
    const fd = new FormData(form);
    const data = {};
    fd.forEach((v, k) => { data[k] = v; });
    data.enquiry_type = 'installer-referral';
    if (token) data.recaptchaToken = token;
    try {
      const res = await apiFetch('/api/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) { setSubmitted(true); form.reset(); }
      else {
        const err = await res.json().catch(() => ({}));
        setSubmitError(err.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    }
    recaptchaReset();
    setSubmitting(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // See useInvisibleRecaptcha: a dismissed, expired or blocked challenge never
  // calls onVerify, which would otherwise leave the button stuck.
  const handleRecaptchaUnavailable = useCallback((reason) => {
    setSubmitting(false);
    setSubmitError(
      reason === 'blocked'
        ? "We couldn't load the spam check - an ad blocker or privacy extension is usually the cause. Please allow it for this page and try again, or email info@asgshading.com.au or call +61 422 439 499."
        : reason === 'expired'
          ? 'The spam check timed out. Please try again.'
          : 'The spam check could not be completed. Please try again, or email info@asgshading.com.au.'
    );
  }, []);

  const { containerRef: recaptchaRef, execute: recaptchaExecute, reset: recaptchaReset, prepare: loadRecaptcha } =
    useInvisibleRecaptcha({ siteKey: RECAPTCHA_SITE_KEY, onVerify: submitForm, onUnavailable: handleRecaptchaUnavailable });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    recaptchaExecute();
  };

  return (
    <div className="fi-page">
      <SEO
        title="Information for Your Installer"
        description="What your installer needs to quote a supply-only ASG awning: substrate and loads, bracket spacing, manpower and electrical. Manual and wind report to download."
        keywords="awning installation information, awning installer Melbourne, retractable awning installation manual, supply only awning"
        canonical="/for-installers"
        jsonLd={[SEO.breadcrumbSchema([{ name: 'For Installers' }]), SEO.webPageSchema({ type: 'WebPage', name: 'Information for your installer', description: 'What an installer needs to quote and fit a supply-only ASG awning.', url: '/for-installers' })]}
      />

      <section className="fi-hero">
        <div className="fi-hero-content">
          <p className="fi-label">For Installers</p>
          <h1 className="fi-title">Information for your installer.</h1>
          <p className="fi-subtitle">
            ASG supplies the unit. Your own tradesperson installs it. This page has everything
            they need to quote the job properly and fit it correctly.
          </p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'For Installers' }]} />

      <div className="fi-body">
        <div className="fi-inner">

          <section className="fi-section">
            <h2>How it works</h2>
            <p>
              We are supply only. We manufacture the awning to your dimensions, fabric and finish,
              and deliver it to your door. We do not install, we do not attend or assess sites, and
              we do not arrange council approvals or permits.
            </p>
            <p>
              Most customers fit the awning themselves - it is about an hour's work with a helper,
              the motor plugs into a power point, and any handyman can do it. If you would rather
              use a tradesperson, you use one you already trust and you are not paying a margin on
              someone else's labour. Either way, whoever does the fixing takes responsibility for
              it, so it matters that they have the right information first - which is what this
              page is for.
            </p>
          </section>

          <section className="fi-section fi-video">
            <h2>Watch the installation</h2>
            <p>
              The full AW01 fit in three minutes: measuring and marking the wall, fixing the
              brackets, lifting the cassette on, plugging it in, and setting the pitch. In real time
              it is about an hour with a helper. Watch it before deciding whether to do it yourself,
              or send the link to your handyman.
            </p>
            <ProjectVideo video={AW01_INSTALL_VIDEO} title={AW01_INSTALL_VIDEO.title} mode="guide" />
          </section>

          <section className="fi-section">
            <h2>Documents to download</h2>
            <p>
              Free to download, no email required. Send these to your installer before they quote.
            </p>
            {documents.length > 0 ? (
              <ProductDocuments documents={documents} />
            ) : (
              <p className="fi-muted">
                Documents are available on request - email{' '}
                <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a>.
              </p>
            )}
          </section>

          <section className="fi-section">
            <h2>What your installer needs before quoting</h2>

            <div className="fi-req">
              <h3>Substrate and load capacity</h3>
              <p>
                The entire load is carried at the bracket points - {BRACKET_COUNT} - so the anchor at each has to
                reach the structure. Concrete, solid brick and structural timber take the supplied
                anchors directly. Hollow or cavity brick needs a chemical anchor with a sieve sleeve
                that engages the structural leaf. Rendered or clad walls are fixed through the
                surface into the structure behind - drill a pilot hole first to confirm what is
                there. Mud brick, or a wall you cannot identify, needs engineering advice before
                drilling.
              </p>
            </div>

            <div className="fi-req">
              <h3>Bracket spacing</h3>
              <p>
                Bracket positions are not free choice - they are set by the arm base positions for
                the ordered width and projection. Allow 6.5 cm from the end of the cassette to the
                outer bracket position. The wall must be able to take the load at those specific
                points, so the substrate needs checking where the brackets actually land, not just
                somewhere along the wall.
              </p>
            </div>

            <div className="fi-req">
              <h3>Manpower</h3>
              <p>
                At least <strong>two competent people</strong> are required. <strong>Three or
                more</strong> for awnings 4 metres wide or more, or for any lift above shoulder
                height. This is separate from delivery: units over 3 metres in width or projection
                also need two people on hand simply to receive them.
              </p>
            </div>

            <div className="fi-req">
              <h3>Electrical</h3>
              <p>
                Motorised units come with a <strong>standard plug</strong> and run from a normal
                power point - there is no hard-wiring. An electrician is only needed if there is no
                power point within reach and one has to be installed. Integrated LED lighting, where
                fitted, runs at 24V from the unit. Allow for the power point position when quoting -
                it is easier to sort before the unit goes up.
              </p>
            </div>

            <div className="fi-req">
              <h3>Operating limits</h3>
              <p>
                The awning is a shade product. It must be retracted in wind above Beaufort force 6
                (roughly 39 to 49 km/h), and in hail or snow. The AW01 is wind tested by Intertek to
                EN 13561 Class 3 at the tested sample size. Full operating limits are in the manual.
              </p>
            </div>
          </section>

          <section className="fi-section fi-referral" id="find-installer">
            <h2>Need help finding an installer?</h2>
            <p>
              We do not employ or endorse installers, and we do not publish a list. But tell us
              where you are and what you have ordered, and we will point you in a sensible
              direction where we can.
            </p>

            {submitted ? (
              <div className="fi-success">
                <p><strong>Thanks - we have got your details.</strong></p>
                <p>We will be in touch shortly. If it is urgent, call us on +61 422 439 499.</p>
              </div>
            ) : (
              <form className="fi-form" ref={formRef} onSubmit={handleSubmit} onFocus={loadRecaptcha}>
                <input type="hidden" name="category" value="awning" />
                <div className="fi-form-row">
                  <label className="fi-field">
                    <span>Name</span>
                    <input type="text" name="full_name" required />
                  </label>
                  <label className="fi-field">
                    <span>Email</span>
                    <input type="email" name="email" required />
                  </label>
                </div>
                <div className="fi-form-row">
                  <label className="fi-field">
                    <span>Suburb</span>
                    <input type="text" name="suburb" required />
                  </label>
                  <label className="fi-field">
                    <span>Postcode</span>
                    <input type="text" name="postcode" inputMode="numeric" maxLength={4} required />
                  </label>
                </div>
                <div className="fi-form-row">
                  <label className="fi-field">
                    <span>Product</span>
                    <select name="model" defaultValue="">
                      <option value="" disabled>Select</option>
                      <option value="AW01 Full Cassette Awning">AW01 Full Cassette Awning</option>
                      <option value="B06 Premium Open-Arm Awning">B06 Premium Open-Arm Awning</option>
                      <option value="B05 Traditional Scalloped Awning">B05 Traditional Scalloped Awning</option>
                      <option value="A09 Long-Projection Awning">A09 Long-Projection Awning</option>
                      <option value="K3 Heritage Mediterranean Awning">K3 Heritage Mediterranean Awning</option>
                      <option value="Aluminium pergola">Aluminium pergola</option>
                      <option value="Not decided yet">Not decided yet</option>
                    </select>
                  </label>
                  <label className="fi-field">
                    <span>Preferred timing</span>
                    <select name="message" defaultValue="">
                      <option value="" disabled>Select</option>
                      <option value="Preferred timing: as soon as possible">As soon as possible</option>
                      <option value="Preferred timing: within a month">Within a month</option>
                      <option value="Preferred timing: 1 to 3 months">1 to 3 months</option>
                      <option value="Preferred timing: just planning ahead">Just planning ahead</option>
                    </select>
                  </label>
                </div>
                {RECAPTCHA_SITE_KEY && <div ref={recaptchaRef}></div>}
                {submitError && <p className="fi-error" role="alert">{submitError}</p>}
                <button type="submit" className="fi-submit" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Ask us about installers'}
                </button>
                <p className="fi-form-note">
                  We will only use these details to respond to your enquiry. We do not pass them to
                  third parties.
                </p>
              </form>
            )}
          </section>

          <div className="fi-cta">
            <Link to="/measuring-guide" className="fi-btn-secondary">How to measure</Link>
            <Link to="/request-quote" className="fi-btn-primary">Request a quote</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
